import config from "csi/config";
import def from "csi/def";
import FieldConstructor, { Field } from "csi/def/Field";
import Relationship from "csi/def/Relationship";
import Widget from "csi/form/Widget";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import _ from "underscore";

const PALETTE_TO_SEASON = "PaletteToSeason";
const PALETTE_TO_SEASON_ALL_SEASONS = "ThemeToSeason:AllSeasons";
const PALETTE_TO_SEASON_SINGLE_SEASON = "ThemeToSeason:SingleSeason";

const ColorPaletteForm = declare(Widget, {

	SUBTYPE: "Subtype",
	SEASONS: "Seasons",
	CAT1_NAMES: "Category1Names",
	CAT2_NAMES: "Category2Names",
	COLLECTION_NAMES: "CollectionNames",

	/**
	 * Handle Seasons field on change of subtype
	 * @param {defView} view- view
	 * @param {field} field - field on the form
	 * @param {value} string - value of the field
	 */
	_onChangeSubtype: function(defView, field, value) {
		const seasonsField = this.defView.getField(this.SEASONS);
		if (!seasonsField) {
			return;
		}

		// When Subtype selected is "Single Season", then Seasons field should be single-select
		const isSingleSeason = xml.isValidURL(value) && config.getSubtypeSetting(value, PALETTE_TO_SEASON) == PALETTE_TO_SEASON_SINGLE_SEASON;
		const newType = isSingleSeason ? xml.ATTR_REF : xml.ATTR_REFSET;
		const newTypeControl = isSingleSeason ? def.FIELD_FILTERING_SELECT : def.FIELD_MULTI_SELECT;

		if (newType != seasonsField.m_type) {
			seasonsField.m_type = newType;
			seasonsField.m_typeControl = newTypeControl;

			this._rebuildField(seasonsField);
		}

		this.checkAllSeasons(seasonsField, value);
	},

	/**
	 * Determines if the Subtype is "All Seasons" and disables the Seasons field if so
	 * @param {field} seasonsField - the Season field object
	 * @param {value} subtypeURL - url of the Color Palette's subtype
	 */
	checkAllSeasons: function(seasonsField, subtypeURL) {
		const isAllSeasons = xml.isValidURL(subtypeURL) && config.getSubtypeSetting(subtypeURL, PALETTE_TO_SEASON) == PALETTE_TO_SEASON_ALL_SEASONS;
		this.setFieldDisabled(seasonsField, isAllSeasons);
		if (isAllSeasons) {
			this.setFieldValue(this.SEASONS, []);
		}
	},

	/**
	 * Handle Cat1, Cat2 and collection fields on change of seasons
	 * @param {defView} view- view
	 * @param {field} field - field on the form
	 * @param {value} string - value of the field
	 */
	_onChangeSeasons(defView, field, value) {
		this.setFieldStore(this.CAT1_NAMES, this.getFieldStore(defView.getField(this.CAT1_NAMES), null));
		this.setFieldStore(this.CAT2_NAMES, this.getFieldStore(defView.getField(this.CAT2_NAMES), null));
		this.setFieldStore(this.COLLECTION_NAMES, this.getFieldStore(defView.getField(this.COLLECTION_NAMES), null));
	},

	/**
	 * @override
	 * Add the Subtype and seasons field if not added from form definition
	 */
	_resolveView: function() {
		this.inherited(arguments);

		// Insert Subtype field if it hasn't been added to the FormDefinition
		let fieldSubtype = this.defView.getField(this.SUBTYPE);
		if (!fieldSubtype) {
			fieldSubtype = this.insertField(lang.mixin(new FieldConstructor(), {
				m_id: "Subtype",
				m_bRequired: true,
			}), 0);
		}
		fieldSubtype.m_onChange = "_onChangeSubtype";

		// Insert Seasons field if it hasn't been added to the FormDefinition
		let fieldSeasons = this.defView.getField(this.SEASONS);
		if (!fieldSeasons) {
			fieldSeasons = this.insertField(lang.mixin(new FieldConstructor(), {
				m_id: "Seasons",
				m_bRequired: true,
			}), 1);
		}
		fieldSeasons.m_onChange = "_onChangeSeasons";
	},

	/*
	 * @override
	 * Build field stores for cat1 , cat2 and collection fields
	 */
	getFieldStore: function(field, value) {
		const ctx = this.getContext();
		const seasonsField = this.defView.getField(this.SEASONS);
		const seasonsVal = seasonsField.getValue();
		if (seasonsVal && (field.getControlName() == this.CAT1_NAMES || field.getControlName() == this.CAT2_NAMES || field.getControlName() == this.COLLECTION_NAMES)) {
			const q = new Query();
			if (seasonsField.m_typeControl == def.FIELD_MULTI_SELECT) {
				const qOr = q.Or();
				_.each(seasonsVal, (season) => {
					qOr.RefAttr("ParentSeason", xml.OP_EQUALS, season);
				});
			} else {
				q.RefAttr("ParentSeason", xml.OP_EQUALS, seasonsVal);
			}

			const type = field.getControlName().split("N")
				.first();
			// create relationship for cat1, cat2 and collection names fields
			const rel = lang.mixin(new Relationship(), {
				m_typeNode: type,
				m_query: q,
			});

			return ctx.view.createHeadingNamesStore(ctx, field, null, rel);
		}

		return this.inherited(arguments);
	},

	/**
	 * Enable hierarchy fields for editing
	 * @override
	 */
	_updateFieldVisibility(field: Field) {
		this.inherited(arguments);
		if (this.isEdit() || this.isCopy()) {
			const fieldSubtype = this.defView.getField(this.SUBTYPE);
			const subtypeValue = fieldSubtype && fieldSubtype.getValue();
			this._onChangeSubtype(this.defView, fieldSubtype, subtypeValue);
			const isAllSeasons = xml.isValidURL(subtypeValue) && config.getSubtypeSetting(subtypeValue, PALETTE_TO_SEASON) == PALETTE_TO_SEASON_ALL_SEASONS;
			this.setFieldDisabled(this.SEASONS, isAllSeasons);
			this.setFieldDisabled(this.CAT1_NAMES, false);
			this.setFieldDisabled(this.CAT2_NAMES, false);
			this.setFieldDisabled(this.COLLECTION_NAMES, false);
		}
		return true;
	},


});

export = ColorPaletteForm;